import { scanText } from "@boem/core/guard";
import { useEffect, useRef, type ReactNode } from "react";

export function Modal({ onClose, children, wide }: { onClose: () => void; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input,textarea,select,button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? " modal-wide" : ""}`} role="dialog" aria-modal="true" ref={ref}>
        {children}
      </div>
    </div>
  );
}

/**
 * Warns as someone types if the text looks like it holds a credential.
 * Returns the findings so the caller can block the submit button. The server
 * runs the same check and has the final say.
 */
export function useSecretCheck(...texts: string[]) {
  return texts.flatMap((t) => scanText(t));
}

export function SecretHint({ findings }: { findings: ReturnType<typeof scanText> }) {
  if (!findings.length) return null;
  const labels = [...new Set(findings.map((f) => f.label))];
  return (
    <div className="secret-hint" role="alert">
      <b>Looks like a credential:</b> {labels.join(", ")} ({findings.map((f) => f.preview).join(", ")}). Remove it or replace it
      with a placeholder like <code>&lt;token&gt;</code> &mdash; it can&rsquo;t be saved to the board.
    </div>
  );
}

export function ErrorLine({ error }: { error: string | null }) {
  return <div className={`msg${error ? " err" : ""}`}>{error ?? ""}</div>;
}
